import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import { useForm, Controller } from 'react-hook-form';
import { createTransfer } from '../../services/Transfer/transferService';
import ErrorModal from '../Modals/ErrorModal';
import SuccessModal from '../Modals/SuccessModal';
import DateInput from '../Utils/DateInput';
import HarvestInput from '../Utils/HarvestInput';
import PersonInput from '../Utils/PersonInput';
import FarmInput from '../Utils/FarmInput';
import SiloInput from '../Utils/SiloInput';
import ProductInput from '../Utils/ProductInput';
import SelectInput from '../Utils/SelectInput';
import WeightInput from '../Utils/WeightInput';
import InputField from '../Utils/InputField';
import { convertDateToISO, getCurrentDate } from '../Utils/utils';
import { Operations } from '../Enum/OperationsEnum';

const CreateTransferModal = ({ show, handleClose, refreshTransfers }) => {
  const { register, handleSubmit, formState: { errors }, reset, setValue, control } = useForm({
    defaultValues: {
      transferDate: getCurrentDate(),
    }
  });
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      const payload = {
        ...data,
        harvestId: data?.harvestId ? data.harvestId.id : null,
        originPersonId: data.originPersonId.id,
        destinyPersonId: data?.destinyPersonId ? data.destinyPersonId.id : null,
        siloId: data?.siloId ? data.siloId.id : null,
        farmId: data?.farmId ? data.farmId.id : null,
        productId: data.productId.id,
        transferDate: data.transferDate ? convertDateToISO(data.transferDate) : null,
      }

      await createTransfer(payload);
      setSuccessMessage('Transferência realizada com sucesso!');
      setShowSuccessModal(true);
      reset();
    } catch (error) {
      setErrorMessage(error.message || 'Erro ao realizar transferência.');
      setShowErrorModal(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseErrorModal = () => setShowErrorModal(false);
  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    refreshTransfers();
    handleClose();
  };

  useEffect(() => {
    if (!show) {
      reset();
    }
  }, [show, reset]);

  return (
    <>
      <Modal show={show} onHide={handleClose} centered backdrop="static" keyboard={false} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Nova Transferência</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit(onSubmit)}>
            <Row>
              <Col md={3}>
                <Controller
                  name="transferDate"
                  control={control}
                  rules={{ required: 'Data é obrigatória' }}
                  render={({ field, fieldState }) => (
                    <DateInput
                      isEditing = { false }
                      label="Data"
                      value={field.value || getCurrentDate()}
                      onChange={field.onChange}
                      error={fieldState.error}
                      placeholder="__/__/____"
                    />
                  )}
                />
              </Col>
              <Col md={9}>
                <Controller
                  name="harvestId"
                  control={control}
                  render={({ field, fieldState }) => (
                    <HarvestInput
                      value={field.value}
                      createdNew={true}
                      onChange={(selected) => {
                        setValue('harvestId', selected);
                      }}
                      onCreated={(created) => {
                        setValue('harvestId', { id: created.id, name: created.name });
                      }}
                      label="Safra"
                    />
                  )}
                />
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Controller
                  name="originPersonId"
                  control={control}
                  rules={{ required: 'Origem é obrigatório' }}
                  render={({ field, fieldState }) => (
                    <PersonInput
                      value={field.value}
                      createdNew={true}
                      onChange={(selected) => setValue('originPersonId', selected)}
                      onCreated={(created) => {
                        setValue('originPersonId', { id: created.id, name: created.name });
                      }}
                      label="Origem"
                      isInvalid={!!fieldState.error}
                      type={['isCustomer', 'isProducer', 'isSupplier' , 'isDriver']}
                    />
                  )}
                />
              </Col>
              <Col md={6}>
                <Controller
                  name="destinyPersonId"
                  control={control}
                  rules={{
                    required: 'Destino é obrigatório',
                  }}
                  render={({ field, fieldState }) => (
                    <PersonInput
                      value={field.value}
                      createdNew={true}
                      onChange={(selected) => setValue('destinyPersonId', selected)}
                      onCreated={(created) => {
                        setValue('destinyPersonId', { id: created.id, name: created.name });
                      }}
                      label="Destino"
                      isInvalid={!!fieldState.error}
                      type={['isCustomer', 'isProducer', 'isSupplier' , 'isDriver']}
                    />
                  )}
                />
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Controller
                  name="siloId"
                  control={control}
                  render={({ field, fieldState }) => (
                    <SiloInput
                      value={field.value}
                      createdNew={true}
                      onChange={(selected) => {
                        setValue('siloId', selected);
                      }}
                      onCreated={(created) => {
                        setValue('siloId', { id: created.id, name: created.name });
                      }}
                      label="Silo"
                    />
                  )}
                />
              </Col>
              <Col md={6}>
                <Controller
                  name="farmId"
                  control={control}
                  render={({ field, fieldState }) => (
                    <FarmInput
                      value={field.value}
                      createdNew={true}
                      onChange={(selected) => {
                        setValue('farmId', selected);
                      }}
                      onCreated={(created) => {
                        setValue('farmId', { id: created.id, name: created.name });
                      }}
                      label="Fazenda"
                    />
                  )}
                />
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Controller
                  name="productId"
                  control={control}
                  rules={{ required: 'Cultura é obrigatório' }}
                  render={({ field, fieldState }) => (
                    <ProductInput
                      value={field.value}
                      createdNew={true}
                      onChange={(selected) => {
                        setValue('productId', selected);
                      }}
                      onCreated={(created) => {
                        setValue('productId', { id: created.id, name: created.name });
                      }}
                      label="Cultura"
                      isInvalid={!!fieldState.error}
                    />
                  )}
                />
              </Col>
              <Col md={6}>
                <Controller
                  name="type"
                  control={control}
                  rules={{ required: 'Operação é obrigatória' }}
                  render={({ field }) => (
                    <SelectInput
                      data={Operations}
                      value={field.value}
                      onChange={(selected) => field.onChange(selected)}
                      label="Operação"
                      isInvalid={!!errors.type}
                      errorMessage={errors.type?.message}
                    />
                  )}
                />
              </Col>
            </Row>
            <Row>
            <Col md={3}>
                <Controller
                  name="quantity"
                  control={control}
                  rules={{ required: 'Quantidade é obrigatória' }}
                  render={({ field, fieldState }) => (
                    <WeightInput
                      value={field.value}
                      onChange={field.onChange}
                      label="Quantidade"
                      isInvalid={!!fieldState.error}
                      placeholder="0.000"
                      errorMessage={errors.operation?.message}
                    />
                  )}
                />
              </Col>
              <Col md={9}>
                <InputField
                  label="Observações"
                  name="description"
                  placeholder="Observações"
                  register={register}
                  errors={errors}
                />
              </Col>   
            </Row>
            <Button variant="primary" className="button-custom w-100 mt-3" type="submit" disabled={isSubmitting}>
              Cadastrar
            </Button>
          </Form>
        </Modal.Body>
      </Modal>

      <ErrorModal
        show={showErrorModal}
        handleClose={handleCloseErrorModal}
        errorMessage={errorMessage}
      />

      <SuccessModal
        show={showSuccessModal}
        handleClose={handleCloseSuccessModal}
        successMessage={successMessage}
      />
    </>
  );
};

export default CreateTransferModal;